import type { ScaleId } from "@engine";
import type { Shape } from "../level/types";
import type { SongId } from "../audio/songs";

// A LevelTheme drives the global "palette" of a single run / level:
// scene background, distance fog, ambient + key light tone, baseline
// bloom strength, sky tunnel star colours, melodic key/scale for the
// music director, and (optionally) per-shape color overrides on the
// existing tunnel materials.
//
// Themes are picked at the start of every story level, on entry to
// endless mode (cycled), and deterministically by date for daily mode.

export interface ShapeTint {
  primary?: number;
  emissive?: number;
}

export interface SkyConfig {
  tintA: number;
  tintB: number;
  density: number;
}

// MusicConfig now carries the song identity (which curated song the
// MusicDirector should play) AND legacy root/scale fields. The curated
// song wins -- root + scale here only matter as a fallback for themes
// that don't pick a specific song.
export interface MusicConfig {
  songId: SongId;
  rootHz: number;
  scaleId: ScaleId;
}

export interface LevelTheme {
  id: string;
  background: number;
  fogColor: number;
  fogNear: number;
  fogFar: number;
  ambient: { color: number; intensity: number };
  key: { color: number; intensity: number };
  // Multiplier applied to BLOOM_BASE in MorphHexGame. 1 = neutral.
  bloomStrength: number;
  shapeTint?: Partial<Record<Shape, ShapeTint>>;
  sky?: SkyConfig;
  music?: MusicConfig;
}

// Sensible fallbacks for themes that don't override sky / music.
export const DEFAULT_SKY: SkyConfig = {
  tintA: 0x2bd3ff,
  tintB: 0xff5cb1,
  density: 56,
};

export const DEFAULT_MUSIC: MusicConfig = {
  songId: "neon",
  rootHz: 55, // A1 -- classic dubstep sub root
  scaleId: "minor",
};

// "neon": original look — cool blue/pink, neutral lights.
const neon: LevelTheme = {
  id: "neon",
  background: 0x06070b,
  fogColor: 0x06070b,
  fogNear: 25,
  fogFar: 90,
  ambient: { color: 0xffffff, intensity: 0.6 },
  key: { color: 0xffffff, intensity: 0.4 },
  bloomStrength: 1,
  sky: { tintA: 0x2bd3ff, tintB: 0xff5cb1, density: 56 },
  music: { songId: "neon", rootHz: 55, scaleId: "minor" },
};

// "dawn": warm amber sky, soft gold lights, octagons skew a bit warmer.
const dawn: LevelTheme = {
  id: "dawn",
  background: 0x1a0e08,
  fogColor: 0x2a1408,
  fogNear: 22,
  fogFar: 85,
  ambient: { color: 0xffd9a8, intensity: 0.55 },
  key: { color: 0xffba6b, intensity: 0.55 },
  bloomStrength: 1.05,
  shapeTint: {
    octagon: { primary: 0xff9966, emissive: 0x6c2912 },
  },
  sky: { tintA: 0xffba6b, tintB: 0xff5cb1, density: 48 },
  music: { songId: "dawn", rootHz: 130.81, scaleId: "minor" }, // C3 (in song's mid range)
};

// "storm": slate / electric blue with a cool key light. High bloom.
const storm: LevelTheme = {
  id: "storm",
  background: 0x070912,
  fogColor: 0x0a0e1c,
  fogNear: 20,
  fogFar: 80,
  ambient: { color: 0xb8c6ff, intensity: 0.5 },
  key: { color: 0x6e8bff, intensity: 0.5 },
  bloomStrength: 1.2,
  shapeTint: {
    octagon: { primary: 0x6ecbff, emissive: 0x0e3a66 },
    triangle: { primary: 0xa166ff, emissive: 0x2c0e66 },
  },
  sky: { tintA: 0x6ecbff, tintB: 0xa166ff, density: 64 },
  music: { songId: "storm", rootHz: 49.0, scaleId: "minor" }, // G1
};

// "void": deep purple-black, faint magenta lights. Low ambient = high
// contrast for the climactic story finale.
const voidTheme: LevelTheme = {
  id: "void",
  background: 0x05020a,
  fogColor: 0x0a0414,
  fogNear: 18,
  fogFar: 75,
  ambient: { color: 0xc6a8ff, intensity: 0.42 },
  key: { color: 0xff5cb1, intensity: 0.5 },
  bloomStrength: 1.3,
  shapeTint: {
    square: { primary: 0xff6b9c, emissive: 0x4d0a2c },
    triangle: { primary: 0xff5cb1, emissive: 0x661454 },
  },
  sky: { tintA: 0xff5cb1, tintB: 0x6e3aff, density: 72 },
  music: { songId: "void", rootHz: 46.25, scaleId: "phrygian" }, // F#1
};

// "sunset": muted coral / teal — endless rotation flavor.
const sunset: LevelTheme = {
  id: "sunset",
  background: 0x1a0a14,
  fogColor: 0x2a1020,
  fogNear: 22,
  fogFar: 88,
  ambient: { color: 0xffa8b8, intensity: 0.55 },
  key: { color: 0x6bd2c6, intensity: 0.5 },
  bloomStrength: 1.1,
  shapeTint: {
    octagon: { primary: 0x6bd2c6, emissive: 0x0e4a44 },
    square: { primary: 0xff9b6b, emissive: 0x4a2008 },
  },
  sky: { tintA: 0x6bd2c6, tintB: 0xff9b6b, density: 56 },
  music: { songId: "sunset", rootHz: 146.83, scaleId: "minor" }, // D3
};

// "forest": deep teal-green sky tinted toward the new hexagon shape.
// Bumps the hexagon emissive so the new polygon visually owns this
// palette; everything else inherits the per-shape defaults.
const forest: LevelTheme = {
  id: "forest",
  background: 0x041208,
  fogColor: 0x06180c,
  fogNear: 22,
  fogFar: 90,
  ambient: { color: 0xa8ffcb, intensity: 0.55 },
  key: { color: 0x6bff9c, intensity: 0.5 },
  bloomStrength: 1.1,
  shapeTint: {
    hexagon: { primary: 0x6bff9c, emissive: 0x144a2c },
  },
  sky: { tintA: 0x6bff9c, tintB: 0xa8ffcb, density: 60 },
  music: { songId: "matrix", rootHz: 61.74, scaleId: "minor" }, // B1
};

// "magenta": deep berry sky tinted toward the new pentagon shape.
// Same idea: dial up the pentagon emissive so it reads as the hero
// polygon when this theme rolls.
const magenta: LevelTheme = {
  id: "magenta",
  background: 0x100416,
  fogColor: 0x18062a,
  fogNear: 20,
  fogFar: 85,
  ambient: { color: 0xffb8e0, intensity: 0.55 },
  key: { color: 0xff66dd, intensity: 0.55 },
  bloomStrength: 1.2,
  shapeTint: {
    pentagon: { primary: 0xff66dd, emissive: 0x4a1444 },
  },
  sky: { tintA: 0xff66dd, tintB: 0x6e3aff, density: 64 },
  music: { songId: "void", rootHz: 46.25, scaleId: "phrygian" }, // F#1
};

// "matrix": deep green CRT vibe — endless rotation flavor.
const matrix: LevelTheme = {
  id: "matrix",
  background: 0x040a06,
  fogColor: 0x081410,
  fogNear: 20,
  fogFar: 82,
  ambient: { color: 0xa8ffc6, intensity: 0.55 },
  key: { color: 0x6bff9c, intensity: 0.5 },
  bloomStrength: 1.15,
  shapeTint: {
    octagon: { primary: 0x6bff9c, emissive: 0x0e4a2c },
    square: { primary: 0xb8ff5c, emissive: 0x3a4a14 },
    triangle: { primary: 0x5cffa8, emissive: 0x14663a },
  },
  sky: { tintA: 0x6bff9c, tintB: 0xb8ff5c, density: 80 },
  music: { songId: "matrix", rootHz: 61.74, scaleId: "minor" }, // B1
};

export const THEMES: Record<string, LevelTheme> = {
  neon,
  dawn,
  storm,
  void: voidTheme,
  sunset,
  matrix,
  forest,
  magenta,
};

// Story per-level mapping. Index N -> theme for level N+1.
//   1 neon    - opening warmup, classic palette
//   2 dawn    - introduces shield/slowmo with a soft sunrise vibe
//   3 storm   - pulse trick storm in cool greys
//   4 void    - first finale, deep purple climax
//   5 forest  - hex mastery; the new hexagon-tinted palette anchors it
//   6 magenta - final run; berry-toned sky for the apex_gate boss
export const STORY_THEMES: string[] = [
  "neon",
  "dawn",
  "storm",
  "void",
  "forest",
  "magenta",
];

// Endless mode rotates through this list as new chunks come in. The
// rotation index is owned by ThemeService. Forest + magenta are
// interleaved so the new shapes get fresh palettes during endless runs.
export const ENDLESS_THEMES: string[] = [
  "neon",
  "sunset",
  "forest",
  "matrix",
  "magenta",
  "storm",
];

// Daily mode picks one of these deterministically from the date seed.
export const DAILY_THEMES: string[] = [
  "neon",
  "dawn",
  "storm",
  "void",
  "sunset",
  "matrix",
  "forest",
  "magenta",
];

export function themeById(id: string): LevelTheme {
  return THEMES[id] ?? neon;
}
